import { useEffect, useState } from 'react';
import { Table, Tag, Skeleton } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import Collapsible from '../Settings/Collapsible/Collapsible';
import { getCookie } from '../../utils/cookies';
import { getDeviceHistory, getAuditLog } from '../../services/security.service';
import type { DeviceHistoryEntry, AuditLogEntry } from '../../types/security';
import styles from './Activity.module.css';

const PAGE_SIZE = 10;

const TRUST_STATE_COLOURS: Record<DeviceHistoryEntry['trustState'], string> = {
  new: 'blue',
  trusted: 'green',
  suspicious: 'orange',
  blocked: 'red',
};

const deviceColumns: ColumnsType<DeviceHistoryEntry> = [
  {
    title: 'Device',
    key: 'device',
    render: (_, r) => `${r.browser ?? 'Unknown browser'} on ${r.os ?? 'Unknown OS'}`,
  },
  {
    title: 'Type',
    dataIndex: 'deviceType',
    key: 'deviceType',
    render: (v: string | null) => v ?? '—',
  },
  {
    title: 'Last IP',
    dataIndex: 'lastIp',
    key: 'lastIp',
    render: (v: string | null) => v ?? '—',
  },
  {
    title: 'First Seen',
    dataIndex: 'firstSeen',
    key: 'firstSeen',
    render: (v: string) => new Date(v).toLocaleString(),
  },
  {
    title: 'Last Seen',
    dataIndex: 'lastSeen',
    key: 'lastSeen',
    render: (v: string) => new Date(v).toLocaleString(),
  },
  {
    title: 'Status',
    dataIndex: 'trustState',
    key: 'trustState',
    render: (v: DeviceHistoryEntry['trustState']) => (
      <Tag color={TRUST_STATE_COLOURS[v]}>{v.toUpperCase()}</Tag>
    ),
  },
];

const auditColumns: ColumnsType<AuditLogEntry> = [
  {
    title: 'Event',
    dataIndex: 'event',
    key: 'event',
    render: (v: string) => v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
  },
  {
    title: 'Date / Time',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (v: string) => new Date(v).toLocaleString(),
  },
  {
    title: 'IP',
    dataIndex: 'ip',
    key: 'ip',
    render: (v: string | null) => v ?? '—',
  },
  {
    title: 'Details',
    dataIndex: 'metadata',
    key: 'metadata',
    render: (v: Record<string, unknown> | null | undefined) => {
      if (!v) return '—';
      const entries = Object.entries(v);
      if (entries.length === 0) return '—';
      return entries.map(([k, val]) => `${k}: ${val}`).join(', ');
    },
  },
];

const Activity = () => {
  const token = getCookie('firebase_token') as string;

  const [devices, setDevices] = useState<DeviceHistoryEntry[]>([]);
  const [devicesTotal, setDevicesTotal] = useState(0);
  const [devicesPage, setDevicesPage] = useState(1);
  const [devicesLoading, setDevicesLoading] = useState(true);

  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLoading, setAuditLoading] = useState(true);

  useEffect(() => {
    setDevicesLoading(true);
    getDeviceHistory(token, devicesPage, PAGE_SIZE)
      .then((result) => {
        setDevices(result.data);
        setDevicesTotal(result.count);
      })
      .catch(() => setDevices([]))
      .finally(() => setDevicesLoading(false));
  }, [devicesPage]);

  useEffect(() => {
    setAuditLoading(true);
    getAuditLog(token, auditPage, PAGE_SIZE)
      .then((result) => {
        setAuditLog(result.data);
        setAuditTotal(result.count);
      })
      .catch(() => setAuditLog([]))
      .finally(() => setAuditLoading(false));
  }, [auditPage]);

  const handleDevicesPageChange = (pagination: TablePaginationConfig) => {
    setDevicesPage(pagination.current ?? 1);
  };

  const handleAuditPageChange = (pagination: TablePaginationConfig) => {
    setAuditPage(pagination.current ?? 1);
  };

  return (
    <div className={styles.container}>
      <Collapsible title="Device History" expanded={true}>
      
        {devicesLoading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : (
          <Table
            dataSource={devices}
            columns={deviceColumns}
            rowKey={(r) => `${r.browser}-${r.os}-${r.firstSeen}`}
            pagination={{ current: devicesPage, pageSize: PAGE_SIZE, total: devicesTotal }}
            onChange={handleDevicesPageChange}
            locale={{ emptyText: 'No devices on record yet' }}
            size="small"
          />
        )}
      </Collapsible>

      <Collapsible title="Security Audit Log" expanded={true}>
        {auditLoading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : (
          <Table
            dataSource={auditLog}
            columns={auditColumns}
            rowKey={(r) => r.createdAt + r.event}
            pagination={{ current: auditPage, pageSize: PAGE_SIZE, total: auditTotal }}
            onChange={handleAuditPageChange}
            locale={{ emptyText: 'No audit events on record yet' }}
            size="small"
          />
        )}
      </Collapsible>
    </div>
  );
};

export default Activity;
